import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Grid, Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from "@mui/material";
import { useApi } from "../../../common/hooks";
import { PortfolioSearchService } from "../../../api";
import { CircularLoader, Preview } from "../../../common/components";
import { translate } from "../../../common/providers";
import SearchRow from "../../../common/components/row/search-row/SearchRow";
import style from "./SearchPortfolioContent.module.css";
import { isNonEmptyArray } from "../../../common/utils";
import PortfolioTabSearch from "../../dashboards/dashboard-portfolio/portfolio-space/components/PortfolioTabSearch";
import { preparePayload } from "../../dashboards/dashboard-portfolio/portfolio-space/utils/utils";
import {
	resetAllFiltersPortfolioSpace,
	setTableHead,
} from "../../dashboards/dashboard-portfolio/portfolio-space/slice/portfolio-space-slice";

const hasActiveFilters = (filters) =>
	!!filters?.content || Object.keys(filters || {}).some((key) => key !== "content" && key !== "projectStatus");

export default function SearchPortfolioContent({ pagePortfolio }) {
	const { call: getMultipleSearchByPortfolio } = useApi(PortfolioSearchService.getMultipleSearchByPortfolio);
	const { call: getMyPortfoliosFilters } = useApi(PortfolioSearchService.getMyPortfoliosFilters);
	const { filters, sorting } = useSelector(
		({ portfolioSpace }) => ({
			filters: portfolioSpace?.filters,
			sorting: portfolioSpace?.sorting,
		}),
		shallowEqual
	);
	const dispatch = useDispatch();
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [pageDataTable, setPageDataTable] = useState(0);
	const [loadingResults, setLoadingResults] = useState(false);
	const [result, setResult] = useState();
	const [preview, setPreview] = useState(null);
	const handleCloseDisplay = () => setPreview(null);
	const callApiSearch = (params) => {
		setLoadingResults(true);
		getMultipleSearchByPortfolio(params)
			.then((data) => {
				setResult(data);
			})
			.catch(console.error)
			.finally(() => setLoadingResults(false));
	};

	const handleChangePage = (event, newPage) => {
		setPageDataTable(newPage);
		const params = {
			page: newPage,
			limit: rowsPerPage,
			portfolioId: pagePortfolio.id,
			payload: preparePayload(filters, sorting),
		};
		callApiSearch(params);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		setPageDataTable(0);
		const params = {
			page: 0,
			limit: event.target.value,
			portfolioId: pagePortfolio.id,
			payload: preparePayload(filters, sorting),
		};
		callApiSearch(params);
	};

	const handleDisplay = ({ informationId, documentId, page, informationType, projectId, companyId }) =>
		setPreview({
			coordinates:
				result?.details?.contents?.find((res) => res.informationId === informationId)?.rectangleDTO || [],
			documentId,
			page,
			informationId,
			informationType,
			companyId,
			projectId,
		});

	useEffect(
		() => () => {
			dispatch(resetAllFiltersPortfolioSpace({}));
		},
		[dispatch]
	);

	useEffect(() => {
		if (pagePortfolio?.id) {
			getMyPortfoliosFilters(pagePortfolio.id)
				.then((data) => dispatch(setTableHead(data)))
				.catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagePortfolio?.id]);

	useEffect(() => {
		if (!pagePortfolio?.id) {
			return;
		}
		setPageDataTable(0);
		if (hasActiveFilters(filters)) {
			const payload = preparePayload(filters, sorting);
			callApiSearch({ page: 0, limit: rowsPerPage, portfolioId: pagePortfolio.id, payload });
		} else {
			setResult(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, sorting]);

	const isContract = pagePortfolio?.name === "Contrats";
	const columnCount = isContract ? 3 : 2;
	return (
		<Grid container>
			<Grid container item md={12}>
				<PortfolioTabSearch
					searchPlaceholder={translate("common:portfolio.search.input.text.label.keyword")}
					showArchivedSwitch={false}
				/>
			</Grid>
			<Grid className={style.tableContainer}>
				<Table className={style.table}>
					<TableHead>
						<TableCell>{translate("common:new-portfolio:project-name")}</TableCell>
						{isContract && <TableCell>Co-contractor</TableCell>}
						<TableCell>Content</TableCell>
					</TableHead>
					<TableBody>
						{(loadingResults && (
							<TableRow>
								<TableCell align="center" colSpan={columnCount}>
									<CircularLoader />
								</TableCell>
							</TableRow>
						)) || (
							<>
								{(isNonEmptyArray(result?.details?.contents) &&
									result.details.contents.map((row) => (
										<TableRow>
											<TableCell>{row.projectName}</TableCell>
											{isContract && <TableCell>{row.coContracting}</TableCell>}
											<TableCell>
												<SearchRow
													companyId={row.companyId}
													content={row.content}
													documentId={row.documentId}
													documentName={row.documentName}
													informationId={row.informationId}
													page={row.page}
													rowProjectId={row.projectId}
													onDisplay={handleDisplay}
												/>
											</TableCell>
										</TableRow>
									))) || (
									<TableRow>
										<TableCell align="center" colSpan={columnCount}>
											{filters?.content
												? translate("common:search-portfolio-filter.no-content")
												: translate("common:search-portfolio-filter.no-filter")}
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</Grid>
			<Grid container alignContent="flex-end" justifyContent="flex-end">
				<TablePagination
					component="div"
					count={result?.details?.totalElements || 0}
					labelRowsPerPage={translate("common:pagination.row-per-page")}
					page={pageDataTable}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[5, 25, 50, 75]}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Grid>
			<Preview
				custom
				companyId={preview?.companyId}
				coordinates={preview?.coordinates}
				docId={preview?.documentId}
				infoId={preview?.informationId}
				infoType={preview?.informationType}
				open={!!preview}
				page={preview?.page}
				projectId={preview?.projectId}
				onClose={handleCloseDisplay}
			/>
		</Grid>
	);
}
